import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, Plus, ArrowRight, Copy, Package, Settings, Truck, Home as HomeIcon, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useClinicAuth } from "../context/ClinicAuthContext";
import { clinicListOrders, formatUsd, apiClient } from "../lib/api";

const STATUS_STYLES = {
  pending: "bg-[#F5E4D6] text-[#8E4038]",
  paid: "bg-[#C5CFB6] text-[#4F5F3F]",
  shipped: "bg-[#F0DFD0] text-[#2A1F1F]",
  delivered: "bg-[#2A1F1F] text-[#FBF6F1]",
};

export default function ClinicDashboardPage() {
  const { clinic, loading, logout, updateProfile } = useClinicAuth();
  const [orders, setOrders] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [tab, setTab] = useState("orders");
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const nav = useNavigate();

  const loadOrders = () => clinicListOrders().then(setOrders).catch(() => {});
  const loadPricing = () => apiClient.get("/clinics/pricing").then((r) => setPricing(r.data)).catch(() => {});

  useEffect(() => {
    if (clinic) {
      setProfile({
        clinic_name: clinic.clinic_name || "",
        contact_name: clinic.contact_name || "",
        tagline: clinic.tagline || "",
        brand_color: clinic.brand_color || "#B85C56",
        brand_logo_url: clinic.brand_logo_url || "",
        phone: clinic.phone || "",
      });
      loadOrders();
      loadPricing();
    }
  }, [clinic]);

  const advance = async (ref, next) => {
    try {
      await apiClient.patch(`/clinics/orders/${ref}/status`, { status: next });
      toast.success(`Order marked ${next}`);
      loadOrders();
    } catch { toast.error("Could not update status"); }
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;
  if (!clinic) return <Navigate to="/clinics/login" replace />;

  const doLogout = () => { logout(); nav("/clinics"); toast.success("Signed out"); };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profile);
      toast.success("Clinic profile updated");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const copyTrackLink = async (ref) => {
    const url = `${window.location.origin}/order-track/${ref}`;
    try { await navigator.clipboard.writeText(url); toast.success("Patient tracking link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
      <div className="flex flex-col sm:flex-row justify-between gap-4 items-start sm:items-end pb-8 border-b border-[#F0DFD0]">
        <div>
          <div className="eyebrow">Clinic Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight" data-testid="clinic-dashboard-title">
            {clinic.clinic_name}
          </h1>
          {clinic.tagline && <div className="text-sm text-[#6B5757] mt-2">{clinic.tagline}</div>}
        </div>
        <div className="flex gap-2">
          <Link to="/clinics/new-order" data-testid="clinic-cta-new-order" className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Patient Order
          </Link>
          <button data-testid="clinic-logout" onClick={doLogout} className="btn-outline inline-flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-6 border-b border-[#F0DFD0]">
        <button
          data-testid="clinic-tab-orders"
          onClick={() => setTab("orders")}
          className={`pb-3 text-sm font-medium transition-colors ${tab === "orders" ? "border-b-2 border-[#B85C56] text-[#2A1F1F]" : "text-[#A08D8D]"}`}
        >
          <Package className="w-4 h-4 inline mr-2" /> Patient Orders ({orders.length})
        </button>
        <button
          data-testid="clinic-tab-settings"
          onClick={() => setTab("settings")}
          className={`pb-3 text-sm font-medium transition-colors ${tab === "settings" ? "border-b-2 border-[#B85C56] text-[#2A1F1F]" : "text-[#A08D8D]"}`}
        >
          <Settings className="w-4 h-4 inline mr-2" /> Brand Settings
        </button>
      </div>

      {tab === "orders" && (
        <div className="mt-10 space-y-6">
          {pricing && (
            <div data-testid="clinic-tier-badge" className="soft-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FDF3ED] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#B85C56]" />
                </div>
                <div>
                  <div className="eyebrow">Your Volume Tier</div>
                  <div className="font-serif text-2xl mt-1">{pricing.current_tier.label}</div>
                  <div className="text-xs text-[#6B5757] mt-1">
                    {pricing.month_count} order{pricing.month_count === 1 ? "" : "s"} this month
                    {pricing.next_tier && ` · ${pricing.next_tier.min - pricing.month_count} more to reach ${pricing.next_tier.label}`}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {pricing.tiers.slice().reverse().map((t) => (
                  <span key={t.min} className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest rounded-full ${
                    t.min === pricing.current_tier.min ? "bg-[#B85C56] text-white" : "bg-[#F0DFD0] text-[#6B5757]"
                  }`}>{t.min}+ · {Math.round(t.discount * 100)}% off</span>
                ))}
              </div>
            </div>
          )}
          {orders.length === 0 ? (
            <div className="border border-dashed border-[#E5CDBB] rounded-lg p-16 text-center">
              <p className="font-serif text-2xl text-[#6B5757]">No patient orders yet</p>
              <Link to="/clinics/new-order" className="btn-primary inline-block mt-6">Place Your First Order</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.order_ref} data-testid={`clinic-order-${o.order_ref}`} className="bg-white border border-[#F0DFD0] rounded-lg p-6 flex flex-col md:flex-row gap-4 md:items-center">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-mono text-xs tracking-widest text-[#B85C56]">{o.order_ref}</div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[o.status] || STATUS_STYLES.pending}`}>
                        {o.status}
                      </span>
                      {o.discount_pct > 0 && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDF3ED] text-[#B85C56]">
                          {o.tier_label}
                        </span>
                      )}
                    </div>
                    <div className="font-serif text-xl mt-2 text-[#2A1F1F]">{o.patient_name}</div>
                    <div className="text-sm text-[#6B5757] mt-1">{o.product_name}</div>
                    {o.swaps.length > 0 && (
                      <div className="mt-2 text-[11px] font-mono tracking-wide text-[#B85C56] uppercase">
                        ↳ Customized: {o.swaps.map(s => s.brand).join(" · ")}
                      </div>
                    )}
                    {o.surgeon_notes && (
                      <div className="mt-3 text-xs italic text-[#6B5757] max-w-lg">"{o.surgeon_notes}"</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:flex md:flex-col gap-2 text-right md:text-left">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">Surgery</div>
                      <div className="font-serif text-sm">{o.surgery_date}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">Delivery</div>
                      <div className="font-serif text-sm">{o.delivery_date}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">Total</div>
                      <div className="font-serif text-sm">{formatUsd(o.price)}</div>
                      {o.discount_pct > 0 && (
                        <div className="text-[10px] text-[#3E6B52]">−{o.discount_pct}% off {formatUsd(o.list_price)}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 md:min-w-[10rem]">
                    <button
                      data-testid={`clinic-order-copy-link-${o.order_ref}`}
                      onClick={() => copyTrackLink(o.order_ref)}
                      className="inline-flex items-center gap-2 px-3 py-2 border border-[#2A1F1F] text-xs uppercase tracking-widest hover:bg-[#2A1F1F] hover:text-[#FBF6F1] transition-colors rounded-full"
                    >
                      <Copy className="w-3 h-3" /> Patient Link
                    </button>
                    {o.status === "pending" && (
                      <button data-testid={`clinic-order-ship-${o.order_ref}`} onClick={() => advance(o.order_ref, "shipped")}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest bg-[#2A1F1F] text-[#FBF6F1] rounded-full hover:bg-[#B85C56]">
                        <Truck className="w-3 h-3" /> Mark Shipped
                      </button>
                    )}
                    {o.status === "shipped" && (
                      <button data-testid={`clinic-order-deliver-${o.order_ref}`} onClick={() => advance(o.order_ref, "delivered")}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest bg-[#B85C56] text-[#FBF6F1] rounded-full hover:bg-[#2A1F1F]">
                        <HomeIcon className="w-3 h-3" /> Mark Delivered
                      </button>
                    )}
                    <Link to={`/order-track/${o.order_ref}`} className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest text-[#6B5757] hover:text-[#B85C56]">
                      Preview <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-10 max-w-2xl">
          <div className="soft-card p-8">
            <div className="eyebrow">Co-Branded Patient Experience</div>
            <p className="text-sm text-[#6B5757] mt-2 max-w-md">
              These details appear on the tracking page every patient sees when they follow the link you send them.
            </p>

            <div className="mt-8 space-y-4">
              {[
                { key: "clinic_name", label: "Clinic Name" },
                { key: "contact_name", label: "Surgeon Name" },
                { key: "tagline", label: "Tagline (e.g. Board-Certified · Miami, FL)" },
                { key: "phone", label: "Phone" },
                { key: "brand_logo_url", label: "Logo URL (optional)" },
              ].map((f) => (
                <label key={f.key} className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">{f.label}</span>
                  <input
                    data-testid={`clinic-settings-${f.key}`}
                    value={profile[f.key] || ""}
                    onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                    className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
                  />
                </label>
              ))}
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Brand Colour</span>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    data-testid="clinic-settings-brand_color"
                    type="color"
                    value={profile.brand_color || "#B85C56"}
                    onChange={(e) => setProfile({ ...profile, brand_color: e.target.value })}
                    className="w-14 h-12 border border-[#E5CDBB] rounded-md bg-white cursor-pointer"
                  />
                  <span className="font-mono text-xs text-[#6B5757]">{profile.brand_color}</span>
                </div>
              </label>
            </div>

            <button
              data-testid="clinic-settings-save"
              onClick={saveProfile} disabled={saving}
              className="btn-primary mt-8 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Brand"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
